import React from 'react';
import MdiIcon from '@mdi/react';
import {
  mdiFormatColorFill,
  mdiFormatColorText,
  mdiFormatSubscript,
  mdiFormatSuperscript,
  mdiMinus,
  mdiTableRemove
} from '@mdi/js';
import type { Editor } from '@tiptap/core';
import { Icon } from 'semantic-ui-react';

import { setFontFamily, setFontSize, setHighlightColor, setTextColor, unsetFontFamily, unsetFontSize } from './commands';
import { TIPTAP_FONT_FAMILIES, TIPTAP_FONT_SIZES } from './constants';
import { ToolbarButton } from './ToolbarButton';

type TiptapToolbarProps = {
  editor: Editor;
  onIndent: () => void;
  onOutdent: () => void;
  onPromptForImage: () => void;
  onPromptForLink: () => void;
  onUploadImage: () => void;
};

export const Toolbar = ({
  editor,
  onIndent,
  onOutdent,
  onPromptForImage,
  onPromptForLink,
  onUploadImage
}: TiptapToolbarProps) => {
  return (
    <div className='tiptapToolbar'>
      <div className='tiptapToolbarGroup'>
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          ariaLabel='Undo'
        >
          <Icon name='undo' />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          ariaLabel='Redo'
        >
          <Icon name='redo' />
        </ToolbarButton>
      </div>

      <div className='tiptapToolbarGroup'>
        <ToolbarButton
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          ariaLabel='Bold'
        >
          <Icon name='bold' />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          ariaLabel='Italic'
        >
          <Icon name='italic' />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          ariaLabel='Underline'
        >
          <Icon name='underline' />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          ariaLabel='Strike'
        >
          <Icon name='strikethrough' />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('subscript')}
          onClick={() => editor.chain().focus().toggleSubscript().run()}
          ariaLabel='Subscript'
        >
          <MdiIcon path={mdiFormatSubscript} size={0.72} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('superscript')}
          onClick={() => editor.chain().focus().toggleSuperscript().run()}
          ariaLabel='Superscript'
        >
          <MdiIcon path={mdiFormatSuperscript} size={0.72} />
        </ToolbarButton>
      </div>

      <div className='tiptapToolbarGroup'>
        <ToolbarButton
          active={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          ariaLabel='Heading 1'
        >
          H1
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          ariaLabel='Heading 2'
        >
          H2
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          ariaLabel='Heading 3'
        >
          H3
        </ToolbarButton>
      </div>

      <div className='tiptapToolbarGroup'>
        <ToolbarButton
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          ariaLabel='Bulleted List'
        >
          <Icon name='list ul' />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          ariaLabel='Numbered List'
        >
          <Icon name='list ol' />
        </ToolbarButton>
        <ToolbarButton
          onClick={onOutdent}
          ariaLabel='Outdent'
        >
          <Icon name='outdent' />
        </ToolbarButton>
        <ToolbarButton
          onClick={onIndent}
          ariaLabel='Indent'
        >
          <Icon name='indent' />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          ariaLabel='Align Left'
        >
          <Icon name='align left' />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          ariaLabel='Align Center'
        >
          <Icon name='align center' />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          ariaLabel='Align Right'
        >
          <Icon name='align right' />
        </ToolbarButton>
      </div>

      <div className='tiptapToolbarGroup tiptapToolbarGroupSelects'>
        <select
          className='tiptapToolbarSelect'
          value={editor.getAttributes('textStyle').fontFamily || ''}
          onChange={e => {
            const value = e.target.value;

            if (!value) {
              unsetFontFamily(editor);
              return;
            }

            setFontFamily(editor, value);
          }}
        >
          <option value=''>Font</option>
          {TIPTAP_FONT_FAMILIES.map(fontFamily => (
            <option key={fontFamily} value={fontFamily}>
              {fontFamily}
            </option>
          ))}
        </select>
        <select
          className='tiptapToolbarSelect'
          value={editor.getAttributes('textStyle').fontSize || ''}
          onChange={e => {
            const value = e.target.value;

            if (!value) {
              unsetFontSize(editor);
              return;
            }

            setFontSize(editor, value);
          }}
        >
          <option value=''>Size</option>
          {TIPTAP_FONT_SIZES.map(fontSize => (
            <option key={fontSize} value={fontSize}>
              {fontSize}
            </option>
          ))}
        </select>
        <label className='tiptapToolbarColorField' title='Text Color'>
          <span className='tiptapToolbarColorLabel'>
            <MdiIcon path={mdiFormatColorText} size={0.72} />
          </span>
          <input
            className='tiptapToolbarColor'
            type='color'
            value={editor.getAttributes('textStyle').color || '#2a2b2e'}
            onChange={e => setTextColor(editor, e.target.value)}
            aria-label='Text Color'
          />
        </label>
        <label className='tiptapToolbarColorField' title='Highlight Color'>
          <span className='tiptapToolbarColorLabel'>
            <MdiIcon path={mdiFormatColorFill} size={0.72} />
          </span>
          <input
            className='tiptapToolbarColor'
            type='color'
            value={editor.getAttributes('textStyle').backgroundColor || '#ffffff'}
            onChange={e => setHighlightColor(editor, e.target.value)}
            aria-label='Highlight Color'
          />
        </label>
      </div>

      <div className='tiptapToolbarGroup'>
        <ToolbarButton
          active={editor.isActive('link')}
          onClick={onPromptForLink}
          ariaLabel='Link'
        >
          <Icon name='linkify' />
        </ToolbarButton>
        <ToolbarButton onClick={onPromptForImage} ariaLabel='Image'>
          <Icon name='image' />
        </ToolbarButton>
        <ToolbarButton
          onClick={onUploadImage}
          ariaLabel='Upload Image'
        >
          <Icon name='upload' />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('table')}
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          ariaLabel='Insert Table'
        >
          <Icon name='table' />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().deleteTable().run()}
          ariaLabel='Remove Table'
        >
          <MdiIcon path={mdiTableRemove} size={0.72} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          ariaLabel='Horizontal Rule'
        >
          <MdiIcon path={mdiMinus} size={0.72} />
        </ToolbarButton>
      </div>
    </div>
  );
};
